import { useMemo } from 'react';
import type { ChatSession } from '../services/chatService';
import type { Persona } from '../types/persona';

export interface RecentChatPersona {
  persona: Persona;
  lastActivity: string;
}

/**
 * Session-derived Recents for the New Chat picker. Distinct from the
 * localStorage-based useRecentPersonas (Trailhead). Answers "who did I
 * recently chat with" from real chat sessions.
 */
export function useRecentChatPersonas(
  sessions: ChatSession[] | undefined,
  personas: Persona[] | undefined,
  limit: number = 5,
): RecentChatPersona[] {
  return useMemo(() => {
    if (!sessions || !personas) return [];
    const personaById = new Map(personas.map(p => [p.id, p]));
    const seen = new Set<string>();
    const result: RecentChatPersona[] = [];

    const sorted = [...sessions]
      .filter(s => (s.message_count ?? 0) > 1) // defensive; mirrors useChatSessions
      .sort((a, b) => new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime());

    for (const session of sorted) {
      if (result.length >= limit) break;
      if (seen.has(session.persona_id)) continue;
      const persona = personaById.get(session.persona_id);
      if (!persona) continue; // drop orphans (deleted personas)
      seen.add(session.persona_id);
      result.push({ persona, lastActivity: session.last_activity });
    }
    return result;
  }, [sessions, personas, limit]);
}
